import { computed, inject, reactive } from 'vue';

export const SHOP_MANAGEMENT_PAGE_SECTIONS_KEY = 'shopManagementPageSections';
export const PAGE_SECTION_JUMP_TOUCH_DEVICE_QUERY = '(hover: none) and (pointer: coarse)';

const asText = (value) => String(value ?? '').trim();

const isMacPlatform = (platform = '') => /mac/i.test(String(platform));

export const currentPageSectionJumpPlatform = () => {
  if (typeof navigator === 'undefined') {
    return '';
  }

  return navigator.userAgentData?.platform || navigator.platform || '';
};

export const pageSectionJumpShortcutPresentation = (
  platform = currentPageSectionJumpPlatform(),
) => (isMacPlatform(platform)
  ? { aria: 'Meta+/', key: '⌘ + /', name: 'Command plus slash' }
  : { aria: 'Control+/', key: 'Ctrl + /', name: 'Control plus slash' });

export const pageSectionJumpShortcutsAvailable = (mediaQueryList = null) => (
  !Boolean(mediaQueryList?.matches)
);

export const isPageSectionJumpShortcut = (
  event = {},
  platform = currentPageSectionJumpPlatform(),
) => {
  const usesMacShortcut = isMacPlatform(platform);
  const usesExpectedModifier = usesMacShortcut
    ? Boolean(event.metaKey) && !event.ctrlKey
    : Boolean(event.ctrlKey) && !event.metaKey;

  return usesExpectedModifier
    && !event.altKey
    && !event.shiftKey
    && !event.isComposing
    && (event.key === '/' || event.code === 'Slash');
};

const normalizeSection = (section) => {
  const id = asText(section?.id);
  const label = asText(section?.label);

  if (!id || !label) {
    return null;
  }

  return {
    ...section,
    id,
    label,
  };
};

const isActiveSection = (section) => {
  if (!section || section.disabled || section.active === false) {
    return false;
  }

  return typeof section.isActive === 'function' ? section.isActive() !== false : true;
};

const sectionCallback = (section) => (
  section?.onJump || section?.focus || section?.command || null
);

/**
 * A page can register one or more ordered, logical containers with its BayHive
 * shell. The shell owns the registry so the PageHeader, dedicated Jump control,
 * and Command Palette present the same currently-active destinations without
 * page-specific wiring.
 *
 * Target shape:
 * { id, label, onJump: () => void, active?: boolean, isActive?: () => boolean }
 */
export const createPageSectionNavigation = () => {
  const registrations = reactive(new Map());

  const sections = computed(() => {
    const seenIds = new Set();
    const activeSections = [];

    registrations.forEach((registeredSections) => {
      (Array.isArray(registeredSections) ? registeredSections : []).forEach((section) => {
        if (!isActiveSection(section) || seenIds.has(section.id)) {
          return;
        }

        seenIds.add(section.id);
        activeSections.push(section);
      });
    });

    return activeSections.map((section, index) => ({
      ...section,
      number: index + 1,
      shortcut: index < 9 ? String(index + 1) : null,
    }));
  });

  const set = (owner, nextSections) => {
    if (!owner) {
      return () => {};
    }

    const normalizedSections = (Array.isArray(nextSections) ? nextSections : [])
      .map(normalizeSection)
      .filter(Boolean);

    if (normalizedSections.length) {
      registrations.set(owner, normalizedSections);
    } else {
      registrations.delete(owner);
    }

    return () => clear(owner);
  };

  const clear = (owner = null) => {
    if (owner === null || owner === undefined) {
      registrations.clear();
      return;
    }

    registrations.delete(owner);
  };

  const resolve = (target) => {
    const activeSections = sections.value;

    if (typeof target === 'number') {
      return activeSections.find((section) => section.number === target) || null;
    }

    if (typeof target === 'string') {
      return activeSections.find((section) => section.id === target) || null;
    }

    if (target?.id) {
      return activeSections.find((section) => section.id === target.id) || null;
    }

    return null;
  };

  const jump = (target) => {
    const section = resolve(target);
    const callback = sectionCallback(section);

    if (!section || typeof callback !== 'function') {
      return false;
    }

    callback(section);
    return true;
  };

  return {
    clear,
    jump,
    sections,
    set,
  };
};

/**
 * Convenience API for page components. Register each page's ordered targets
 * with a stable owner and call the returned cleanup function on unmount.
 */
export const usePageSectionNavigation = () => {
  const navigation = inject(SHOP_MANAGEMENT_PAGE_SECTIONS_KEY, null);
  const emptySections = computed(() => []);

  return {
    clearPageSections: (owner) => navigation?.clear(owner),
    jumpToPageSection: (target) => navigation?.jump(target) || false,
    pageSections: navigation?.sections || emptySections,
    setPageSections: (owner, sections) => navigation?.set(owner, sections) || (() => {}),
  };
};
